import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';

/* ============================================================
   ITP — rencana inspeksi, dan pelaksanaannya.

   Titik TAHAN menghentikan pekerjaan sampai lolos; titik SAKSI
   hanya perlu diberi tahu. Membuat keduanya menahan akan membuat
   orang berhenti memberi tahu sama sekali, jadi bedanya ditampilkan
   dengan jelas.
   ============================================================ */

type ItpRow = {
  id: string;
  code: string;
  title: string;
  status: string;
  discipline: { code: string } | null;
  activity: { activityCode: string } | null;
  _count: { items: number };
};

const POINT_LABEL: Record<string, string> = {
  HOLD: 'Titik tahan',
  WITNESS: 'Titik saksi',
  REVIEW: 'Tinjauan dokumen',
};

const RESULT_CHIP: Record<string, string> = {
  PASS: 'good',
  PASS_WITH_COMMENT: 'warn',
  FAIL: 'crit',
};

const RESULT_LABEL: Record<string, string> = {
  PASS: 'Lolos',
  PASS_WITH_COMMENT: 'Lolos dengan catatan',
  FAIL: 'Gagal',
};

export function Itps({ projectId }: { projectId: string }) {
  const nav = useNavigate();
  const [itps, setItps] = useState<ItpRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ itps: ItpRow[] }>(`/quality/itps?projectId=${projectId}`);
      setItps(r.itps);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load ITPs.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!itps) return <Loading rows={4} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Inspection & test plan</h1>
          <p>Inspeksi hanya bisa diminta terhadap ITP yang sudah disetujui — kriterianya harus mengikat lebih dulu.</p>
        </div>
      </div>

      {itps.length === 0 ? (
        <Empty
          title="Belum ada ITP"
          body="ITP dibuat lewat API hari ini, lengkap dengan titik-titiknya. Layar penyusunannya belum ada."
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Kode</th>
                <th>Judul</th>
                <th>Disiplin</th>
                <th>Aktivitas</th>
                <th>Titik</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {itps.map((i) => (
                <tr key={i.id} onClick={() => nav(`/itps/${i.id}`)}>
                  <td className="num">{i.code}</td>
                  <td>{i.title}</td>
                  <td>{i.discipline?.code ?? '—'}</td>
                  <td>{i.activity?.activityCode ?? '—'}</td>
                  <td>{i._count.items}</td>
                  <td>
                    <span className={`chip ${i.status === 'APPROVED' ? 'good' : ''}`}>{i.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ============================================================ */

type Detail = {
  itp: {
    id: string;
    code: string;
    title: string;
    status: string;
    activity: { activityCode: string; name: string } | null;
    items: {
      id: string;
      seq: number;
      description: string;
      pointType: string;
      acceptanceCriteria: string | null;
      inspections: {
        id: string;
        requestedAt: string;
        scheduledAt: string | null;
        inspectedAt: string | null;
        result: string | null;
        note: string | null;
        inspector: { name: string } | null;
      }[];
    }[];
  };
};

export function ItpDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Detail>(`/quality/itps/${id}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the ITP.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function approve() {
    setBusy(true);
    try {
      await api.post(`/quality/itps/${id}/approve`);
      setNotice('ITP disetujui. Inspeksi sekarang bisa diminta terhadap titik-titiknya.');
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal.');
    } finally {
      setBusy(false);
    }
  }

  async function request(itemId: string) {
    setBusy(true);
    try {
      await api.post(`/quality/items/${itemId}/inspections`, {});
      setNotice('Permintaan inspeksi tercatat.');
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal meminta inspeksi.');
    } finally {
      setBusy(false);
    }
  }

  async function record(inspectionId: string, result: string) {
    const note =
      result === 'FAIL'
        ? window.prompt('Apa yang tidak sesuai? Wajib diisi:')
        : window.prompt('Catatan (opsional):') ?? '';
    if (result === 'FAIL' && !note?.trim()) return;

    setBusy(true);
    try {
      await api.post(`/quality/inspections/${inspectionId}/result`, { result, note });
      setNotice(
        result === 'FAIL'
          ? 'Kegagalan tercatat. Terbitkan NCR kalau ketidaksesuaiannya perlu ditindaklanjuti.'
          : 'Hasil tercatat.',
      );
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal mencatat hasil.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const itp = data.itp;
  const approved = itp.status === 'APPROVED';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{itp.code}</h1>
          <p>
            {itp.title}
            {itp.activity && ` · ${itp.activity.activityCode}`}
          </p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <span className={`chip ${approved ? 'good' : ''}`}>{itp.status}</span>
          {itp.status === 'DRAFT' && (
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={approve}>
              Setujui ITP
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/itps')}>
            Kembali
          </button>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      {!approved && (
        <div className="banner">
          ITP masih draft. Inspeksi tidak bisa diminta sampai ia disetujui — kriteria penerimaannya
          belum mengikat siapa pun.
        </div>
      )}

      {itp.items.map((item) => {
        const latest = item.inspections[0];
        const pending = item.inspections.find((i) => i.result == null);
        return (
          <Panel
            key={item.id}
            title={`${item.seq}. ${item.description}`}
            note={item.acceptanceCriteria ?? undefined}
            wide
          >
            <div className="filters">
              <span className={`chip ${item.pointType === 'HOLD' ? 'crit' : ''}`}>
                {POINT_LABEL[item.pointType] ?? item.pointType}
              </span>
              {latest?.result && (
                <span className={`chip ${RESULT_CHIP[latest.result] ?? ''}`}>
                  {RESULT_LABEL[latest.result] ?? latest.result}
                </span>
              )}
              {approved && !pending && (
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => request(item.id)}>
                  Minta inspeksi
                </button>
              )}
              {pending && (
                <>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => record(pending.id, 'PASS')}
                  >
                    Lolos
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    onClick={() => record(pending.id, 'PASS_WITH_COMMENT')}
                  >
                    Lolos dengan catatan
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    onClick={() => record(pending.id, 'FAIL')}
                  >
                    Gagal
                  </button>
                </>
              )}
            </div>

            {item.inspections.length > 0 && (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Diminta</th>
                      <th>Diperiksa</th>
                      <th>Hasil</th>
                      <th>Pemeriksa</th>
                      <th>Catatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.inspections.map((i) => (
                      <tr key={i.id} className="no-click">
                        <td>{fmtDate(i.requestedAt)}</td>
                        <td>{i.inspectedAt ? fmtDate(i.inspectedAt) : '—'}</td>
                        <td>
                          {i.result ? (
                            <span className={`chip ${RESULT_CHIP[i.result] ?? ''}`}>
                              {RESULT_LABEL[i.result] ?? i.result}
                            </span>
                          ) : (
                            <span className="hint">menunggu</span>
                          )}
                        </td>
                        <td>{i.inspector?.name ?? '—'}</td>
                        <td className="hint">{i.note ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {item.inspections.length > 1 && (
              <p className="panel-foot">
                Pemeriksaan ulang menambah baris, tidak menimpa yang gagal. Itulah yang membuat
                tingkat lolos pertama bisa dihitung sama sekali.
              </p>
            )}
          </Panel>
        );
      })}
    </>
  );
}
